import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck, Lock, KeyRound, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const ENFORCED_ROLES = new Set(["hr", "compliance"]);

export default function SecuritySettings() {
  const { user, refreshMe } = useAuth();
  const [setup, setSetup] = useState(null); // {setup_id, secret, qr_code_data_url}
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [backupCodes, setBackupCodes] = useState([]);
  const [disableCode, setDisableCode] = useState("");

  const enforced = ENFORCED_ROLES.has(user?.role);
  const enabled = !!user?.mfa_enabled;

  useEffect(() => {
    setBackupCodes([]);
  }, [user?.id]);

  const startEnroll = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/setup/start");
      setSetup(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const confirmEnroll = async (e) => {
    e.preventDefault();
    if (!setup) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/setup/confirm", {
        setup_id: setup.setup_id,
        code: code.trim(),
      });
      toast.success("MFA enabled — save your backup codes");
      setBackupCodes(data.backup_codes || []);
      setSetup(null);
      setCode("");
      await refreshMe();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const disableMfa = async () => {
    if (enforced) {
      toast.error(`MFA is mandatory for role '${user.role}'.`);
      return;
    }
    if (!disableCode.trim()) {
      toast.error("Enter your current MFA code to disable.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/mfa/disable", { code: disableCode.trim() });
      toast.success("MFA disabled.");
      setDisableCode("");
      await refreshMe();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="security-settings-page" className="mx-auto max-w-3xl px-6 py-8 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-[color:var(--page-fg)]">Security</h1>
        <p className="mt-1 text-sm text-[color:var(--page-fg-muted)]">
          Two-factor authentication, backup codes, and session controls.
        </p>
      </div>

      <div className="brand-card p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <span
              className="mt-1 flex h-9 w-9 items-center justify-center rounded-xl"
              style={{ backgroundColor: "#14B8B8", color: "#0B0B12" }}
            >
              <ShieldCheck className="h-4 w-4" />
            </span>
            <div>
              <h2 className="text-base font-semibold text-[color:var(--page-fg)]">
                Authenticator app (TOTP)
              </h2>
              <p className="mt-1 text-xs text-[color:var(--page-fg-muted)]">
                Use Authy, 1Password, Microsoft Authenticator, or Google Authenticator.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge
              data-testid="mfa-status-badge"
              variant="outline"
              className="rounded-full"
              style={{
                borderColor: enabled ? "#7DD3A1" : "#FCA5A8",
                color: enabled ? "#7DD3A1" : "#FCA5A8",
                backgroundColor: enabled ? "rgba(125,211,161,0.1)" : "rgba(252,165,168,0.1)",
              }}
            >
              {enabled ? "Enabled" : "Not enabled"}
            </Badge>
            {enforced && (
              <Badge
                variant="outline"
                className="rounded-full"
                style={{ borderColor: "#FFD37A", color: "#FFD37A", backgroundColor: "rgba(255,211,122,0.18)" }}
              >
                Mandatory for {user?.role}
              </Badge>
            )}
          </div>
        </div>

        {!enabled && !setup && (
          <Button
            data-testid="mfa-enroll-start-button"
            onClick={startEnroll}
            disabled={busy}
            className="mt-5 bg-brand-teal text-white hover:bg-brand-tealDark"
          >
            <KeyRound className="mr-1 h-4 w-4" />
            {busy ? "Generating…" : "Enable MFA"}
          </Button>
        )}

        {setup && (
          <form onSubmit={confirmEnroll} className="mt-5 space-y-4" data-testid="mfa-enroll-form">
            <p className="text-sm text-[color:var(--page-fg-secondary)]">
              1. Scan this QR code in your authenticator app, or enter the secret manually.
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <img
                src={setup.qr_code_data_url}
                alt="MFA QR code"
                className="rounded-xl border border-[color:var(--border-subtle)] bg-white p-2"
                style={{ width: 168, height: 168 }}
                data-testid="mfa-qr-image"
              />
              <code
                data-testid="mfa-secret-code"
                className="rounded-lg border border-[color:var(--border-subtle)] px-3 py-2 font-mono text-xs"
                style={{ backgroundColor: "#15151D", color: "#5EE0E0" }}
              >
                {setup.secret}
              </code>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-[color:var(--page-fg-secondary)]">
                2. Enter the 6-digit code your app shows
              </Label>
              <Input
                data-testid="mfa-enroll-code-input"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="123456"
                className="h-11 w-40 rounded-xl text-center font-mono"
              />
            </div>
            <div className="flex gap-2">
              <Button
                type="submit"
                disabled={busy || code.length < 6}
                data-testid="mfa-enroll-confirm-button"
                className="bg-brand-teal text-white hover:bg-brand-tealDark"
              >
                Confirm and enable
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setSetup(null);
                  setCode("");
                }}
              >
                Cancel
              </Button>
            </div>
          </form>
        )}

        {backupCodes.length > 0 && (
          <div
            data-testid="mfa-backup-codes-block"
            className="mt-5 rounded-xl p-4"
            style={{ backgroundColor: "rgba(232,203,142,0.1)", border: "1px solid #E8CB8E" }}
          >
            <p className="text-sm font-semibold text-[color:var(--page-fg)]">
              Save these backup codes — they will not be shown again.
            </p>
            <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-5">
              {backupCodes.map((c) => (
                <code
                  key={c}
                  data-testid={`mfa-backup-code-${c}`}
                  className="rounded-md px-2 py-1 text-center font-mono text-sm"
                  style={{ backgroundColor: "#15151D", color: "#FFE3A4" }}
                >
                  {c}
                </code>
              ))}
            </div>
          </div>
        )}

        {enabled && !setup && (
          <div className="mt-5 grid gap-3 rounded-xl border border-[color:var(--border-subtle)] p-4">
            <div className="flex items-center gap-2 text-sm font-medium text-[color:var(--page-fg)]">
              <Lock className="h-4 w-4 text-brand-teal" />
              Disable MFA
            </div>
            {enforced ? (
              <p className="text-xs text-[color:var(--page-fg-muted)]">
                MFA is mandatory for role <b>{user?.role}</b> and cannot be disabled here.
              </p>
            ) : (
              <>
                <p className="text-xs text-[color:var(--page-fg-muted)]">
                  Enter your current 6-digit code to confirm.
                </p>
                <div className="flex gap-2">
                  <Input
                    data-testid="mfa-disable-code-input"
                    value={disableCode}
                    onChange={(e) => setDisableCode(e.target.value)}
                    placeholder="123456"
                    className="h-10 w-40 rounded-xl text-center font-mono"
                  />
                  <Button
                    onClick={disableMfa}
                    disabled={busy || !disableCode}
                    data-testid="mfa-disable-button"
                    variant="outline"
                    className="border-red-300 text-red-300 hover:bg-red-300/10"
                  >
                    Disable MFA
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      <div className="brand-card p-6">
        <div className="flex items-start gap-3">
          <span
            className="mt-1 flex h-9 w-9 items-center justify-center rounded-xl"
            style={{ backgroundColor: "rgba(20, 184, 184, 0.16)", color: "#5EE0E0" }}
          >
            <RefreshCw className="h-4 w-4" />
          </span>
          <div>
            <h2 className="text-base font-semibold text-[color:var(--page-fg)]">Sessions</h2>
            <p className="mt-1 text-xs text-[color:var(--page-fg-muted)]">
              Access tokens expire every 8 hours; refresh tokens are rotated on every use and
              expire after 30 days. Logging out revokes the refresh token chain.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
